import moment from "moment";
import type { HistoricNarRecommendation } from "../../../../../../shared/api/services/historicNar/types";
import { HistoricNarConfigTableColumnKeys } from "../../../../../../shared/enums/tableColumnKeys";
import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { getHistoricNarIngestionStateBadge } from "../../../../../enums/historicNarIngestionState";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../../utils/stringModifications";
import type { NarAgentGridOrgTab } from "../../narAgentGrid/hooks/useNarAgentGridOrgTabs";
import {
  determineWhichIconToShow,
  formatScore,
  generateDisplayTextWithPrefixLabel,
  readVolume,
  showInfoIcon,
} from "./historicNarConfigTableUtils";
import { getPrimarySource } from "./historicNarSourceUtils";

export const HistoricNarConfigTableHeaders: TableHeader[] = [
  {
    label: "Payer",
    key: HistoricNarConfigTableColumnKeys.Payer,
    order: 1,
    width: 12,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "LOB",
    key: HistoricNarConfigTableColumnKeys.Lob,
    order: 2,
    width: 9,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "J Code",
    key: HistoricNarConfigTableColumnKeys.JCode,
    order: 3,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Drug Name",
    key: HistoricNarConfigTableColumnKeys.DrugName,
    order: 4,
    width: 16,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Month",
    key: HistoricNarConfigTableColumnKeys.Month,
    order: 5,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Volume",
    key: HistoricNarConfigTableColumnKeys.Volume,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Score",
    key: HistoricNarConfigTableColumnKeys.Score,
    order: 7,
    width: 7,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "State",
    key: HistoricNarConfigTableColumnKeys.Status,
    order: 9,
    width: 10,
    sortable: false,
    type: TableCellType.BADGE_WITH_I,
  },
  {
    label: "Source",
    key: HistoricNarConfigTableColumnKeys.Source,
    order: 8,
    width: 16,
    sortable: false,
    type: TableCellType.STRING,
  },
  {
    label: "",
    key: HistoricNarConfigTableColumnKeys.Action,
    order: 10,
    width: 6,
    sortable: false,
    type: TableCellType.ICON,
  },
];

/** Convert Historic NAR recommendations into the shared table row contract. */
export const generateHistoricNarConfigTableData = (
  rows: HistoricNarRecommendation[],
  clientOptions: NarAgentGridOrgTab[],
) =>
  rows.map((row) => {
    const badge = getHistoricNarIngestionStateBadge(row.status ?? "");
    return {
      [HistoricNarConfigTableColumnKeys.Payer]:
        capitalizeWordsSeperatedByUnderScore(row.payer ?? ""),
      [HistoricNarConfigTableColumnKeys.Lob]:
        capitalizeWordsSeperatedByUnderScore(row.line_of_business ?? ""),
      [HistoricNarConfigTableColumnKeys.JCode]: row.j_code ?? "",
      [HistoricNarConfigTableColumnKeys.DrugName]: row.medicine_name ?? "",
      [HistoricNarConfigTableColumnKeys.Month]: row.month
        ? moment(row.month, ["YYYY-MM", "MM/YYYY", moment.ISO_8601]).format(
            "MM/YYYY",
          )
        : "",
      [HistoricNarConfigTableColumnKeys.Volume]: readVolume(
        row.volume_analytics,
      ),
      [HistoricNarConfigTableColumnKeys.Score]: formatScore(row.score),
      [HistoricNarConfigTableColumnKeys.Status]: {
        text: badge.label,
        color: badge.textColor,
        bgColor: badge.bgColor,
        displayText: generateDisplayTextWithPrefixLabel(row, clientOptions),
        maxWidth: "200px",
        showInfoIcon: showInfoIcon(row.status),
      },
      [HistoricNarConfigTableColumnKeys.Source]:
        getPrimarySource(row.volume_analytics?.source_text) || "—",
      [HistoricNarConfigTableColumnKeys.Action]: {
        variant: determineWhichIconToShow(row.status ?? ""),
        rowData: row,
      },
    };
  });
